// src/redux/slices/categoriesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { showNotification } from './notificationSlice';
import API_ROUTES from "../../config/apiRoutes";

// Fetch categories: only send currentPage and limit
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (_, { getState }) => {
    const { token } = getState().auth;
    const filterPayload = {
      currentPage: 1,
      limit: 100
    };
    const response = await axios.post(
      API_ROUTES.CATEGORIES.GET_ALL,
      filterPayload,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    console.log("Fetched categories:", response.data);
    return response.data; // expected format: { data: [ ... ], total, totalPages }
  }
);

// Create category
export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async (categoryData, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.post(
        API_ROUTES.CATEGORIES.CREATE,
        categoryData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data.message || "Failed to create category");
    }
  }
);

// Delete category
export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (categoryId, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.delete(
        API_ROUTES.CATEGORIES.DELETE(categoryId),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      dispatch(showNotification({ message: "Category deleted successfully!", type: "success" }));
      return categoryId;
    } catch (error) {
      const message = error.response?.data.message || "Failed to delete category";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

// Update category
export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ id, data }, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      // Data is now a regular object with base64 image, not FormData
      const payload = {
        categoryName: data.categoryName,
        priorityOrder: data.priorityOrder,
        isActive: true, // Default to active
      };

      // Only include image if provided
      if (data.categoryImage) {
        payload.categoryImage = data.categoryImage;
      }

      const response = await axios.patch(
        API_ROUTES.CATEGORIES.UPDATE(id),
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        }
      );
      dispatch(showNotification({ message: "Category updated successfully!", type: "success" }));
      return response.data;
    } catch (error) {
      const message = error.response?.data.message || "Failed to update category";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState: { categories: [], loading: false, error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        const categoriesData = action.payload.data || [];
        // Group parents (no parentId) and attach their subcategories
        const parents = categoriesData.filter(cat => !cat.parentId);
        parents.forEach(parent => {
          parent.subcategories = categoriesData.filter(cat => cat.parentId === parent._id);
        });
        state.categories = parents;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        const newCategory = action.payload;
        if (newCategory.parentId) {
          const parentCategory = state.categories.find(cat => cat._id === newCategory.parentId);
          if (parentCategory) {
            parentCategory.subcategories = parentCategory.subcategories || [];
            parentCategory.subcategories.push(newCategory);
          }
        } else {
          state.categories.push(newCategory);
        }
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete category
      .addCase(deleteCategory.fulfilled, (state, action) => {
        const categoryId = action.payload;
        state.categories = state.categories
          .filter(category => category._id !== categoryId)
          .map(category => ({
            ...category,
            subcategories: category.subcategories?.filter(subcat => subcat._id !== categoryId),
          }));
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update category
      .addCase(updateCategory.fulfilled, (state, action) => {
        const updatedCategory = action.payload;
        const parentIndex = state.categories.findIndex(cat => cat._id === updatedCategory._id);
        if (parentIndex !== -1) {
          state.categories[parentIndex] = {
            ...state.categories[parentIndex],
            ...updatedCategory,
          };
        } else {
          // Update if it's a subcategory
          state.categories = state.categories.map(parent => {
            if (parent.subcategories) {
              return {
                ...parent,
                subcategories: parent.subcategories.map(subcat =>
                  subcat._id === updatedCategory._id ? updatedCategory : subcat
                )
              };
            }
            return parent;
          });
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default categoriesSlice.reducer;
